import { Title, Stack, TextInput, Switch, Group, Button, Card } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { foldersApi } from "../api/folders.js";
import { notifications } from "@mantine/notifications";
import type { ClientFolder } from "@cas/shared";

export function ClientFolderEdit() {
  const { domain } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const isNew = !domain;

  const { data: existing } = useQuery({
    queryKey: ["folders", domain],
    queryFn: () => foldersApi.list().then(fs => fs.find(f => f.domain === decodeURIComponent(domain ?? ""))),
    enabled: !isNew,
  });

  const form = useForm<Partial<ClientFolder>>({
    initialValues: existing ?? { domain: "", clientDisplayName: "", folderId: "", enabled: true },
  });

  const save = useMutation({
    mutationFn: (v: Partial<ClientFolder>) => foldersApi.upsert(v as ClientFolder),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["folders"] });
      notifications.show({ title: "Saved", message: "Client folder saved", color: "green" });
      nav("/folders");
    },
    onError: (e: any) => notifications.show({ title: "Save failed", message: String(e.body?.errors ?? e), color: "red" }),
  });

  return (
    <Stack maw={720} mx="auto">
      <Title order={2}>{isNew ? "New client folder" : `Edit: ${existing?.clientDisplayName ?? domain}`}</Title>
      <Card withBorder>
        <Stack>
          <TextInput label="Domain" required disabled={!isNew}
                     placeholder="acme.com" {...form.getInputProps("domain")} />
          <TextInput label="Client display name" required {...form.getInputProps("clientDisplayName")} />
          <TextInput label="Outlook folder ID" required
                     placeholder="AAMkAD... (Outlook entry ID)" {...form.getInputProps("folderId")} />
          <Switch label="Enabled" checked={form.values.enabled ?? true}
                  onChange={(e) => form.setFieldValue("enabled", e.currentTarget.checked)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => nav("/folders")}>Cancel</Button>
            <Button onClick={() => save.mutate(form.values)} loading={save.isPending}>Save</Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
